import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  CANVA_CUSTOM_SIZE_URL,
  disconnectCanva,
  exportCanvaDesignPng,
  fetchCanvaConnectServerStatus,
  listCanvaDesigns,
  startCanvaOAuth,
  type CanvaConnectServerStatus,
  type CanvaDesignListItem,
} from '../../services/canvaConnectService';
import { storageService } from '../../services/storageService';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  recommendedWidthPx: number;
  userId: string | null;
  workspaceId: string | null;
};

export const CanvaEmailImportModal: React.FC<Props> = ({
  isOpen,
  onClose,
  recommendedWidthPx,
  userId,
  workspaceId,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<CanvaConnectServerStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [designs, setDesigns] = useState<CanvaDesignListItem[]>([]);
  const [designsLoading, setDesignsLoading] = useState(false);
  const [continuation, setContinuation] = useState<string | null>(null);
  const [exportingId, setExportingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  /** Preview URL was present but the image failed (expired link, network, etc.). */
  const [thumbLoadFailed, setThumbLoadFailed] = useState<Record<string, true>>({});

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    setError(null);
    try {
      const s = await fetchCanvaConnectServerStatus();
      setStatus(s);
    } catch {
      setStatus(null);
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const fetchDesignPage = useCallback(async (cont?: string | null) => {
    return listCanvaDesigns({
      limit: 24,
      continuation: cont || undefined,
    });
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setBanner(null);
      setExportingId(null);
      setThumbLoadFailed({});
      return;
    }
    loadStatus();
  }, [isOpen, loadStatus]);

  useEffect(() => {
    if (!isOpen) return;
    const params = new URLSearchParams(location.search);
    if (params.get('canva_connected') === '1') {
      setBanner('You’re signed in to Canva.');
      params.delete('canva_connected');
      const q = params.toString();
      navigate(`${location.pathname}${q ? `?${q}` : ''}`, { replace: true });
      loadStatus();
    }
  }, [isOpen, location.pathname, location.search, navigate, loadStatus]);

  useEffect(() => {
    if (!isOpen || !status?.connected) return;
    let cancelled = false;
    setDesigns([]);
    setContinuation(null);
    setThumbLoadFailed({});
    setDesignsLoading(true);
    setError(null);
    (async () => {
      const res = await fetchDesignPage(null);
      if (cancelled) return;
      if (!res) {
        setError('We couldn’t load your designs. Try again, or sign in to Canva again.');
        setDesignsLoading(false);
        return;
      }
      setDesigns(res.items);
      setContinuation(res.continuation);
      setDesignsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, status?.connected, fetchDesignPage]);

  const handleConnect = async () => {
    setError(null);
    const returnPath = `${location.pathname}${location.search || ''}`;
    const res = await startCanvaOAuth(returnPath);
    if (!res?.authorizationUrl) {
      setError('We couldn’t start sign-in. Please try again in a moment.');
      return;
    }
    window.location.assign(res.authorizationUrl);
  };

  const handleDisconnect = async () => {
    setError(null);
    const ok = await disconnectCanva();
    if (!ok) {
      setError('We couldn’t sign you out of Canva. Try again.');
      return;
    }
    setDesigns([]);
    setContinuation(null);
    await loadStatus();
  };

  const handleImportDesign = async (item: CanvaDesignListItem) => {
    if (!userId) {
      setError('Please sign in to Scubamail first.');
      return;
    }
    setExportingId(item.id);
    setError(null);
    try {
      const exp = await exportCanvaDesignPng(item.id, recommendedWidthPx);
      if (!exp?.downloadUrl) {
        setError('We couldn’t add that design. Try another one, or sign in to Canva again.');
        return;
      }
      const imgRes = await fetch(exp.downloadUrl);
      if (!imgRes.ok) {
        setError('We couldn’t finish adding that picture. Please try again.');
        return;
      }
      const blob = await imgRes.blob();
      const safeName = `${(item.title || 'canva').replace(/[^\w\s-]/g, '').slice(0, 60) || 'canva'}.png`;
      const file = new File([blob], safeName, { type: blob.type || 'image/png' });
      if (workspaceId) {
        await storageService.uploadEditorImage(file, userId, workspaceId);
      } else {
        await storageService.uploadEditorImage(file, userId, undefined);
      }
      setBanner('Saved to your images. Add an image to your email and choose it from your library.');
    } catch (e) {
      console.error(e);
      setError(e instanceof Error ? e.message : 'Import failed.');
    } finally {
      setExportingId(null);
    }
  };

  if (!isOpen) return null;

  const integrationConfigured = status?.integrationConfigured === true;
  const connected = status?.connected === true;
  const showBrowse = integrationConfigured && connected;
  const showConnectSettings = integrationConfigured && !connected;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/[0.35] backdrop-blur-md px-4 py-6 sm:py-8"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`relative w-full overflow-hidden flex flex-col rounded-[28px] shadow-[0_24px_80px_-16px_rgba(15,23,42,0.18)] ring-1 ring-slate-900/[0.08] ${
          showBrowse
            ? 'max-w-[min(96vw,1240px)] max-h-[min(92vh,920px)] min-h-[min(72vh,640px)]'
            : 'max-w-lg max-h-[90vh]'
        }`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="canva-import-title"
        aria-modal="true"
      >
        <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden rounded-[28px]" aria-hidden>
          <img
            src="/images/canva-modal-decor.svg"
            alt=""
            className="h-full w-full object-cover object-center opacity-[0.85] scale-105"
            draggable={false}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/88 via-white/72 to-cyan-50/35" />
          <div className="absolute -right-24 -top-28 h-72 w-72 rounded-full bg-teal-400/20 blur-3xl" />
          <div className="absolute -left-28 top-1/3 h-64 w-64 rounded-full bg-violet-400/15 blur-3xl" />
          <div className="absolute right-1/4 bottom-0 h-48 w-48 rounded-full bg-amber-300/10 blur-2xl" />
        </div>

        <div className="relative z-10 flex min-h-0 flex-1 flex-col">
        <div className="px-6 sm:px-9 pt-7 pb-5 sm:pt-8 sm:pb-6 shrink-0 bg-white/55 backdrop-blur-xl border-b border-white/50 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 sm:gap-5">
          <div className="min-w-0 flex flex-col items-start justify-center text-left pr-2">
            {showBrowse ? (
              <h2
                id="canva-import-title"
                className="font-semibold text-slate-900 tracking-[-0.02em] text-[1.375rem] sm:text-[1.625rem] leading-tight"
              >
                Your designs
              </h2>
            ) : (
              <h2 id="canva-import-title" className="sr-only">
                Canva import
              </h2>
            )}
            {showBrowse && (
              <p className="text-[13px] text-slate-600 mt-2 leading-relaxed max-w-md">
                Tap a design to add it to your email.
              </p>
            )}
          </div>
          <div className="shrink-0 flex h-11 w-11 sm:h-12 sm:w-12 items-center justify-center rounded-2xl bg-white shadow-md ring-1 ring-slate-200/70 mx-auto">
            <img src="/images/canva-mark.svg" alt="" className="h-8 w-8 sm:h-9 sm:w-9" draggable={false} />
          </div>
          <div className="flex items-center justify-end gap-1.5 sm:gap-2 shrink-0 min-w-0 pl-2">
            {showBrowse && (
              <>
                <button
                  type="button"
                  onClick={async () => {
                    setDesignsLoading(true);
                    setError(null);
                    setThumbLoadFailed({});
                    const res = await fetchDesignPage(null);
                    if (res) {
                      setDesigns(res.items);
                      setContinuation(res.continuation);
                    } else setError('We couldn’t refresh. Try again.');
                    setDesignsLoading(false);
                  }}
                  className="rounded-full px-3.5 py-2 text-[13px] font-medium text-teal-800 bg-teal-50/90 hover:bg-teal-100/90 transition-colors disabled:opacity-50"
                  disabled={designsLoading}
                >
                  Refresh
                </button>
                <button
                  type="button"
                  onClick={handleDisconnect}
                  className="rounded-full px-3.5 py-2 text-[13px] font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100/90 transition-colors hidden sm:inline"
                >
                  Sign out
                </button>
              </>
            )}
            <button
              type="button"
              onClick={onClose}
              className="flex h-9 w-9 items-center justify-center rounded-full text-slate-500 bg-slate-100/90 hover:bg-slate-200/90 hover:text-slate-800 transition-colors"
              aria-label="Close"
            >
              <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        {showBrowse && (
          <div className="px-6 sm:px-9 pb-3 sm:hidden -mt-1 bg-white/40 backdrop-blur-md border-b border-white/40 sm:border-0">
            <button
              type="button"
              onClick={handleDisconnect}
              className="text-[13px] font-medium text-slate-500 hover:text-slate-800 py-1 transition-colors"
            >
              Sign out of Canva
            </button>
          </div>
        )}

        <div
          className={`flex-1 overflow-y-auto text-sm text-slate-700 min-h-0 ${showBrowse ? 'px-6 sm:px-9 py-6 sm:py-7 bg-white/35' : 'px-6 sm:px-8 py-6 sm:py-7 bg-white/30'}`}
        >
          {banner && (
            <div className="mb-5 rounded-2xl bg-teal-50/90 px-4 py-3.5 text-[13px] text-teal-950 font-medium leading-relaxed ring-1 ring-teal-900/[0.06]">
              {banner}
            </div>
          )}
          {error && (
            <div className="mb-5 rounded-2xl bg-rose-50/90 px-4 py-3.5 text-[13px] text-rose-900 leading-relaxed ring-1 ring-rose-900/[0.06]">
              {error}
            </div>
          )}

          {statusLoading && (
            <p className="text-slate-500 text-[13px] text-center py-14 tracking-wide">One moment…</p>
          )}

          {!statusLoading && showBrowse && (
            <div className="space-y-8 min-h-0">
              {designsLoading && designs.length === 0 ? (
                <p className="text-slate-500 text-[13px] text-center py-20 font-medium tracking-wide">Loading…</p>
              ) : designs.length === 0 ? (
                <p className="text-slate-500 text-[13px] text-center py-20 leading-relaxed max-w-xs mx-auto">
                  No designs here yet.
                </p>
              ) : (
                <div
                  className={
                    designs.length === 1
                      ? 'max-w-[200px] mx-auto'
                      : 'grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 justify-items-center'
                  }
                >
                  {designs.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => handleImportDesign(d)}
                      disabled={Boolean(exportingId)}
                      className="group text-left w-full max-w-[200px] rounded-none bg-white overflow-hidden ring-1 ring-slate-200/70 shadow-[0_2px_12px_-2px_rgba(15,23,42,0.06)]
                        hover:ring-slate-300/90 hover:shadow-[0_16px_48px_-12px_rgba(15,23,42,0.12)] hover:-translate-y-0.5
                        focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/80 focus-visible:ring-offset-2
                        transition-[transform,box-shadow,ring-color] duration-200 ease-out disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-[0_2px_12px_-2px_rgba(15,23,42,0.06)]"
                    >
                      <div className="aspect-[4/3] bg-gradient-to-b from-slate-50 to-slate-100/90 flex items-center justify-center overflow-hidden relative">
                        {Boolean(d.thumbnailUrl) && !thumbLoadFailed[d.id] ? (
                          <img
                            src={d.thumbnailUrl || ''}
                            alt=""
                            loading="lazy"
                            decoding="async"
                            onError={() =>
                              setThumbLoadFailed((prev) => (prev[d.id] ? prev : { ...prev, [d.id]: true }))
                            }
                            className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]"
                          />
                        ) : (
                          <div className="flex flex-col items-center justify-center gap-2.5 px-4 py-10 text-center">
                            <div
                              className="w-10 h-10 rounded-none border border-dashed border-slate-300/70 flex items-center justify-center text-slate-400 bg-white/50"
                              aria-hidden
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                />
                              </svg>
                            </div>
                            <span className="text-[11px] text-slate-500 font-medium tracking-wide uppercase">
                              {thumbLoadFailed[d.id] ? 'Preview unavailable' : 'No preview'}
                            </span>
                          </div>
                        )}
                        {exportingId === d.id && (
                          <div className="absolute inset-0 bg-white/75 backdrop-blur-[3px] flex items-center justify-center">
                            <span className="text-[13px] font-semibold text-teal-900 tracking-tight">Adding…</span>
                          </div>
                        )}
                      </div>
                      <div className="px-4 py-3 bg-white">
                        <p className="text-[13px] font-medium text-slate-800 line-clamp-2 leading-snug tracking-tight">
                          {d.title}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {continuation && !designsLoading && (
                <button
                  type="button"
                  onClick={async () => {
                    setDesignsLoading(true);
                    setError(null);
                    const res = await fetchDesignPage(continuation);
                    if (res) {
                      setDesigns((prev) => [...prev, ...res.items]);
                      setContinuation(res.continuation);
                    } else setError('We couldn’t load more. Try again.');
                    setDesignsLoading(false);
                  }}
                  className="w-full py-3 rounded-full text-[13px] font-medium text-slate-700 bg-white ring-1 ring-slate-200/80 shadow-sm hover:bg-slate-50 hover:ring-slate-300/90 transition-all"
                  disabled={designsLoading}
                >
                  More designs
                </button>
              )}
            </div>
          )}

          {!statusLoading && showConnectSettings && (
            <div className="space-y-5">
              <p className="text-[13px] text-slate-600 leading-relaxed">
                Sign in once to see your Canva designs here. You can sign out anytime.
              </p>
              <button
                type="button"
                onClick={handleConnect}
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-[#00C4CC] text-white text-[13px] font-semibold tracking-tight hover:bg-[#00b0b8] transition-colors shadow-md shadow-teal-600/20"
              >
                <img src="/images/canva-mark.svg" alt="" className="h-5 w-5 brightness-0 invert opacity-95" draggable={false} />
                Sign in to Canva
              </button>
            </div>
          )}

          {!statusLoading && !integrationConfigured && (
            <div className="space-y-6">
              <p className="text-[13px] text-slate-600 leading-relaxed">
                Signing in to Canva from here isn’t available yet. You can still use a design you made in Canva by saving it to your
                computer, then adding it to your email.
              </p>
              <ol className="list-decimal pl-4 space-y-2.5 leading-relaxed text-[13px] text-slate-600 marker:text-slate-400">
                <li>In Canva, make your graphic about as wide as your email.</li>
                <li>Download it as a picture.</li>
                <li>Add an image to your email, then upload that picture or choose it from your library.</li>
              </ol>
              <a
                href={CANVA_CUSTOM_SIZE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#00C4CC] text-white text-[13px] font-semibold hover:bg-[#00b0b8] transition-colors shadow-md shadow-teal-600/20"
              >
                <img src="/images/canva-mark.svg" alt="" className="h-5 w-5 brightness-0 invert opacity-95" draggable={false} />
                Open Canva
                <span aria-hidden>↗</span>
              </a>
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
};
